import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Search } from 'lucide-react';

import type { Person, Team } from '../api/types';
import { Badge, EmptyState, Modal } from './ui';

interface PersonPickerModalProps {
  open: boolean;
  onClose: () => void;
  people: readonly Person[];
  teams: readonly Team[];
  /** People already on the project — not pickable (F-11). */
  exclude?: ReadonlySet<string>;
  onPick: (person: Person) => void;
}

/** F-05: an assignee is always picked from the people dictionary — free text is impossible. */
export function PersonPickerModal({ open, onClose, people, teams, exclude, onPick }: PersonPickerModalProps): ReactElement {
  const [search, setSearch] = useState('');
  const teamById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);

  const query = search.trim().toLowerCase();
  const candidates = people.filter((person) => {
    if (exclude?.has(person.id)) return false;
    if (!query) return true;
    return [person.name, person.role ?? '', person.email ?? ''].join(' ').toLowerCase().includes(query);
  });

  const pick = (person: Person) => {
    onPick(person);
    onClose();
    setSearch('');
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        setSearch('');
      }}
      title="Назначить исполнителя"
      description="Исполнитель выбирается из справочника людей."
    >
      <div className="toolbar__field">
        <label className="toolbar__label" htmlFor="person-picker-search">
          Поиск
        </label>
        <div className="search-box">
          <Search size={15} aria-hidden="true" className="search-box__icon" />
          <input
            id="person-picker-search"
            type="search"
            className="input search-box__input"
            placeholder=""
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {candidates.length === 0 ? (
        <EmptyState title="Никого не найдено" hint="Измените поисковый запрос." />
      ) : (
        <ul className="picker-list">
          {candidates.map((person) => (
            <li key={person.id}>
              <button type="button" className="picker-list__item" onClick={() => pick(person)}>
                <span className="picker-list__avatar" aria-hidden="true">
                  {initials(person.name)}
                </span>
                <span className="picker-list__meta">
                  <span className="picker-list__name">{person.name}</span>
                  <span className="picker-list__sub">
                    {[person.role, teamById.get(person.teamId)?.name].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {person.active ? null : <Badge tone="warn">неактивен</Badge>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
